import React, { useEffect, useRef, useState } from 'react'
import 'react-confirm-alert/src/react-confirm-alert.css';
import { toast } from 'react-toastify';
import { getAllParentWithPaginationService } from '@app/services/student';
import DataTable, { TableStyles } from 'react-data-table-component';
import { Button } from '@app/components';
import { Link } from 'react-router-dom';
import { encodeId } from '@app/helpers/base64url';
import { useSelector } from 'react-redux';
import { getAllActiveFranchise } from '@app/services/franchise';
import ReactSelect from 'react-select';
import ExcelJS from "exceljs/dist/es5/exceljs.browser";
import moment from 'moment';
import fileDownload from 'js-file-download';
import { Modal } from 'react-bootstrap';
import { generateImpersonationQR } from '@app/services/impersonation';

const ListParents = () => {
    const [parents, setParents] = useState([]);
    const [franchises, setFranchises] = useState([]);
    const [isLoading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [search, setSearch] = useState({first_name: '',last_name: '', relation: '', email: '', franchise_id: '', status: ''});
    const studentStatus = [{label:'Active', value:'active'}, {label:'Inactive', value:'inactive'}]
    const canAccess = useSelector((state: any) => state.auth.permissions.student);
    const selectedRole = useSelector((state: any) => state.auth.selectedRole);
    const currentUser = useSelector((state: any) => state.auth.currentUser);
    const canImpersonate = ['admin', 'super-admin'].includes(selectedRole?.slug) && currentUser?.impersonation_enabled;
    const selectRef = useRef(null);
    const [impersonationModal, setImpersonationModal] = useState(false);
    const [selectedParent, setSelectedParent] = useState<any>(null);
    const [qrCode, setQrCode] = useState<string>('');
    const [qrLoading, setQrLoading] = useState(false);

    const customStyle: TableStyles = {
        head: {
            style: {
                color: "#5E5873",
                fontSize: "13px",
                fontWeight: 900,
            },
        },
        headCells: {
            style: {
                borderColor: "#E9ECEF",
                textTransform: "uppercase",
                letterSpacing: "1px",
            },
        },
        headRow: {
            style: {
                backgroundColor: "#F7F7F8",
                minHeight: "44px",
                fontWeight: 500,
            },
        },
    };

    const customStylesSelect = {
        dropdownIndicator: () => ({
            display: 'none',
        }),
    }

    useEffect(() => {
        getAllParentService(1, perPage, sortField, sortDirection);
    },[search]);

    useEffect(() => {
        getAllActiveFranchiseData();
    },[]);


    const getAllActiveFranchiseData = async () => {
        try {
            const allActiveFranchiseResp = await getAllActiveFranchise();
            if (allActiveFranchiseResp.success === true) {
                setFranchises(allActiveFranchiseResp.collections);
            } else {
                toast.error(allActiveFranchiseResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const getAllParentService = async (page, perPage, sortField, sortDirection) => {
        try {
            setLoading(true);
            const parentsResp = await getAllParentWithPaginationService(page, perPage, sortField, sortDirection, search);
            if (parentsResp.success === true) {
                setLoading(false);
                setParents(parentsResp.collections.data);
                setTotalRows(parentsResp?.collections?.pagination?.total);
            } else {
                setLoading(false);
                toast.error(parentsResp?.errors[0]?.msg);
            }
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    const openImpersonationModal = async (parent: any) => {
        setSelectedParent(parent);
        setQrCode('');
        setImpersonationModal(true);
        try {
            setQrLoading(true);
            const resp = await generateImpersonationQR(parent.id);
            if (resp?.success) {
                setQrCode(resp.qr_code);
            } else {
                toast.error(resp?.errors?.[0]?.msg || 'Failed to generate QR');
            }
        } catch (error: any) {
            toast.error(error?.message || 'Internal Server Error');
        } finally {
            setQrLoading(false);
        }
    };

    const columns = [
        {
            name: 'First Name',
            selector: (row: { first_name: string;}) => row.first_name,
            sortable: true,
            sortField: 'first_name',
            wrap: true,
        },
        {
            name: 'Last Name',
            selector: (row: {last_name: string;}) => row.last_name ?? '---',
            sortable: true,
            sortField: 'last_name',
            wrap: true,
        },
        {
            name: 'Email',
            selector: (row: { email: string; }) => row.email ? row.email : '---',
            sortable: true,
            sortField: 'email',
            wrap: true,
        },
        {
            name: 'Gender',
            selector: (row: { gender: string; }) => row.gender ? row.gender.charAt(0).toUpperCase()+row.gender.slice(1) : '---',
            sortable: true,
            sortField: 'gender',
            wrap: true,
        },
        {
            name: 'Contact No.',
            selector: (row: { phone: string; }) => row.phone ? row.phone : '---',
            sortable: true,
            sortField: 'phone',
            wrap: true,
        },
        {
            name: 'Franchise',
            selector: (row: { franchise_name: string; }) => row.franchise_name ? row.franchise_name : '---',
            sortable: true,
            sortField: 'franchise_name',
            wrap: true,
        },
        {
            name: 'Student(s)',
            cell: (row: any) => (
                row.students?.length > 0
                    ? <div>
                        {row.students.map((s: any) => (
                            <div key={s.id}>
                                <Link to={`/student/view/${encodeId(s.id)}`}>
                                    {s.first_name} {s.last_name}
                                </Link>
                            </div>
                        ))}
                      </div>
                    : <span className="text-muted">---</span>
            ),
            width: '180px',
            wrap: true,
        },
        {
            name: 'Status',
            selector: (row: { status: string; }) => row.status,
            cell: (row: { status: string; }) => (
                row.status === 'active' ? <span className="badge bg-success">Active</span> : <span className="capitalize badge bg-danger">{row.status}</span>
            ),
            sortable: true,
            sortField: 'status',
        },
        {  
            name: 'Action',
            cell: (row: { id: number; }) => (
              <>
                { canAccess?.edit &&
                    <Link
                    to={`/parent/edit/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="Edit"
                    >
                    <i className="fas fa-pen mr-2"/>
                    </Link>
                }
                { canAccess?.view &&
                    <Link
                    to={`/parent/view/${encodeId(row.id)}`}
                    role="button"
                    state={{ id: row.id }}
                    data-toggle="tooltip" title="View"
                    >
                    <i className="fas fa-eye mr-2"/>
                    </Link>
                }
                { canImpersonate &&
                    <a href="#" onClick={(e) => { e.preventDefault(); openImpersonationModal(row); }} data-toggle="tooltip" title="Impersonate">
                        <i className="fas fa-user-secret mr-2"/>
                    </a>
                }
              </>
            ),
        }
    ];

    const handlePageChange = page => {
        getAllParentService(page, perPage, sortField, sortDirection);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage, page) => {
        getAllParentService(page, newPerPage, sortField, sortDirection);
        setPerPage(newPerPage);
    };

    const handleSort = async (column, sortDirection) => {
        setSortField(column.sortField);
        setSortDirection(sortDirection);
        getAllParentService(currentPage, perPage, column.sortField, sortDirection);
    };

    const handleResetFilter = async () => {
        selectRef.current.setValue('');
        setSearch({first_name: '',last_name: '', relation: '', email: '', franchise_id: '', status: ''});
    };

    const exportCSV = async() => {
        try {
            setLoading(true);
            let records = await getAllParentWithPaginationService(1, totalRows, sortField, sortDirection, search)
            if (records.success === true) {
                setLoading(false);
            } else {
                setLoading(false);
                toast.error(records?.errors[0]?.msg);
                return;
            }

            if((records?.collections?.data).length === 0){
                toast.error('Records not found.')
                return;
            }

            const workbook = new ExcelJS.Workbook();
            workbook.addWorksheet("Parents");
            const worksheet = workbook.getWorksheet('Parents');
            worksheet.columns = [
                { header: "First Name", key: "first_name" },
                { header: "Last Name", key: "last_name" },
                { header: "Email", key: "email" },
                { header: "Gender", key: "gender" },
                { header: "Franchise", key: "franchise_name" },
                { header: "Phone", key: "phone" },
                { header: "Alternate Phone", key: "alt_phone" },
                { header: "Address", key: "address" },
                { header: "Address Line 2", key: "address_line_2" },
                { header: "City", key: "city" },
                { header: "Postcode", key: "postcode" },
                { header: "Country", key: "country_name" },
                { header: "Status", key: "status" },
                { header: "Student(s)", key: "students" }
            ];
            
            (records?.collections?.data).map((row) => {
                row.franchise_name = row.franchise_name !== null ? row.franchise_name : '---';
                row.gender = row.gender !== null ? row.gender.charAt(0).toUpperCase() + row.gender.slice(1) : '---';
                row.phone = (row.phone != null) ? row.phone : '---';
                row.alt_phone = (row.alt_phone != null) ? row.alt_phone : '---';
                row.address_line_2 = (row.address_line_2 != null) ? row.address_line_2 : '---';
                row.postcode = (row.postcode != null) ? row.postcode : '---';
                row.country_name = (row.country_name != null) ? row.country_name : '---';
                row.status = row.status.charAt(0).toUpperCase() + row.status.slice(1);
                row.students = row.students.length > 0 ? ( Array.prototype.map.call(row.students, function(user) { return user.first_name +' '+ user.last_name; }).join(", ") ) : '---';
            })

            worksheet.addRows(records?.collections?.data);
            let filename = 'Parents-' + moment().format('DD-MMM-YYYY') + '.csv';

            workbook.csv.writeBuffer().then(function(buffer) {
                fileDownload(buffer, filename);
            });
        } catch (error: any) {
            setLoading(false);
            toast.error(error?.message || 'Internal Server Error')
        }
    };

    return (
        <div>
            <div>
                {/* Content Header (Page header) */}
                <section className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-2">
                            <div className="col-sm-6">
                                <h1>Manage Parents</h1>
                            </div>
                            <div className="col-sm-6">
                                <ol className="breadcrumb float-sm-right">
                                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                                    <li className="breadcrumb-item active">Manage Parents</li>
                                </ol>
                            </div>
                        </div>
                    </div>{/* /.container-fluid */}
                </section>
                {/* Main content */}
                <section className="content">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: 10 }} className="row">
                                    
                                </div>
                                <div className="card">
                                    <div className="card-header">
                                        <h3 className="card-title">Manage Parents</h3>
                                    </div>
                                    {/* /.card-header */}
                                    <div className="card-body">
                                        <div className="row mb-2">
                                            <div className="col-sm-2">
                                                <input type="text" name='firstName' className="form-control" value={search.first_name} onChange={(e) => setSearch({ ...search, first_name: e.target.value })} placeholder="Search By First Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='lastName' className="form-control" value={search.last_name} onChange={(e) => setSearch({ ...search, last_name: e.target.value })} placeholder="Search By Last Name" />
                                            </div>
                                            <div className="col-sm-2">
                                                <input type="text" name='email' className="form-control" value={search.email} onChange={(e) => setSearch({ ...search, email: e.target.value })} placeholder="Search By Email" />
                                            </div>
                                            <div className="col-sm-2">
                                                <ReactSelect
                                                    ref={selectRef}
                                                    name="franchise_id"
                                                    placeholder="Search By Franchise"
                                                    options={franchises}
                                                    getOptionValue={(option)=>`${option['id']}`}
                                                    getOptionLabel={(option)=>`${option['name']}`}
                                                    onChange={ (option) => { setSearch({ ...search, franchise_id: option['id'] });}}
                                                    // onInputChange={ (option) => {onChangeFranchise(option)}}
                                                    // value={search.franchise_id}
                                                    styles={customStylesSelect}
                                                />
                                            </div>
                                            <div className="col-sm-2">
                                                <select
                                                    className="form-control"
                                                    name="status"
                                                    onChange={(e) => setSearch({ ...search, status: e.target.value })}
                                                    value={search.status}
                                                >
                                                    <option value="">Search By Status</option>
                                                    {studentStatus.map((obj) => (
                                                        <option key={obj.value} value={obj.value}>
                                                            {obj.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="row mb-2">
                                            <div className='col-sm-4'>
                                                <Button type="submit" isLoading={isLoading} onClick={handleResetFilter} className="btn btn-warning mr-2">Reset Filter</Button>
                                                { canAccess['export-parent-csv'] &&
                                                    <Button type="submit" isLoading={isLoading} onClick={exportCSV} className="btn btn-success"><i className="fas fa-file-csv"></i> Export</Button>
                                                }
                                            </div>
                                        </div>

                                        <DataTable
                                            columns={columns}
                                            data={parents}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            paginationDefaultPage={currentPage}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            paginationPerPage={perPage}
                                            paginationRowsPerPageOptions={[5, 10, 20, 30, 50, 100]}
                                            progressPending={isLoading}
                                            sortServer
                                            onSort={handleSort}
                                            customStyles={customStyle}
                                        />
                                    </div>
                                    {/* /.card-body */}
                                </div>
                            </div>
                            {/* /.col */}
                        </div>
                        {/* /.row */}
                    </div>
                    {/* /.container-fluid */}
                </section>
                {/* /.content */}
            </div>

            <Modal show={impersonationModal} onHide={() => setImpersonationModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Impersonation QR Code</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    { selectedParent && (
                        <div className="mb-3">
                            <div className="row">
                                <div className="col-6">
                                    <small className="text-muted">Name</small>
                                    <p className="mb-1 font-weight-bold">{selectedParent.first_name} {selectedParent.last_name}</p>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">Email</small>
                                    <p className="mb-1">{selectedParent.email || '---'}</p>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">Phone</small>
                                    <p className="mb-1">{selectedParent.phone || '---'}</p>
                                </div>
                                <div className="col-6">
                                    <small className="text-muted">Franchise</small>
                                    <p className="mb-1">{selectedParent.franchise_name || '---'}</p>
                                </div>
                                { selectedParent.students?.length > 0 && (
                                    <div className="col-12">
                                        <small className="text-muted">Student(s)</small>
                                        <p className="mb-1">{selectedParent.students.map((s: any) => `${s.first_name} ${s.last_name}`).join(', ')}</p>
                                    </div>
                                )}
                            </div>
                            <hr />
                        </div>
                    )}
                    <div className="text-center">
                        { qrLoading && <div><i className="fas fa-spinner fa-spin fa-2x"/><p className="mt-2 text-muted">Generating QR code...</p></div> }
                        { !qrLoading && qrCode && <img src={qrCode} alt="Impersonation QR Code" style={{ maxWidth: '100%' }} /> }
                        { !qrLoading && !qrCode && <p className="text-muted">No QR code available.</p> }
                        { !qrLoading && qrCode && <p className="text-muted small mt-2">Scan this QR code to log in as this parent. This code is valid for a single session only.</p> }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                </Modal.Footer>
            </Modal>
        </div>
    )
};

export default ListParents;